// =============================================================================
// Device power schedule (SCDS — Hikvision DS-D6055UN-DP)
//
// The panel firmware keeps two separate things:
//
//   1. A weekly plan edited only in the device's own Settings UI. It is the
//      offline fallback and cannot be written through the SDK.
//   2. A single "next off / next on" slot. `InfoTimeApi.setTimeSwitch(off, on)`
//      overwrites that slot and fires even when every weekly entry is disabled.
//      Once it fires the slot reverts to whatever the weekly plan computes.
//
// So the server owns the schedule and the Player re-applies it on every boot
// and once a day. This module is the pure part of that: given the operator's
// weekly plan and "now", decide the one off/on pair to push into the slot.
//
// Times are wall-clock on the device itself, so every computation here runs in
// the host's local timezone. The Player executes on the panel, so that is the
// panel's own clock.
// =============================================================================

/** Minutes since local midnight, or null when the text is not "HH:MM". */
export function parseClock(text: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(text);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

import type { DeviceRuntimeProfile } from './deviceRuntime.js';

type PowerSubject = {
  powerCapable?: boolean | null;
  nativeStatus?: { sdkAvailable?: boolean } | null;
  nativeReportedAt?: string | null;
  runtime?: DeviceRuntimeProfile | null;
};

/** Only an online panel whose native Hikvision SDK answered may be controlled.
 * Unknown (including old clients without the field) is never permission.
 * Device names and editable display types are not hardware evidence. When the
 * server has classified the runtime, that classification must also grant the
 * capability — a fingerprint lock or a non-Hikvision class denies even when
 * the SDK still answers. */
export function isVerifiedHikvisionPowerDevice(device: {
  powerCapable?: boolean | null;
  nativeStatus?: { sdkAvailable?: boolean } | null;
  nativeReportedAt?: string | null;
  runtime?: DeviceRuntimeProfile | null;
}, now = Date.now()): boolean {
  const reported = Date.parse(device.nativeReportedAt ?? '');
  if (device.runtime && device.runtime.deviceClass !== 'unknown' && (device.runtime.deviceClass !== 'hikvision_apk' || !device.runtime.capabilities.power)) return false;
  return device.powerCapable === true && device.nativeStatus?.sdkAvailable === true
    && Number.isFinite(reported) && now - reported >= 0 && now - reported <= 120_000;
}

/** Class-level denial that applies before any liveness check. */
export function deviceRuntimeBlockReason(runtime: DeviceRuntimeProfile | null | undefined, capability: keyof DeviceRuntimeProfile['capabilities']): string | null {
  if (!runtime || runtime.deviceClass === 'unknown') return null;
  if (runtime.deviceClass === 'web_browser') return '웹 브라우저 플레이어에서는 이 기능을 사용할 수 없습니다.';
  if (runtime.deviceClass === 'android_apk') return 'HIKVISION 패널이 아닌 Android 기기에서는 이 기능을 사용할 수 없습니다.';
  if (runtime.fingerprintLocked) return '기기 하드웨어 정보가 등록 시점과 달라 원격 제어가 잠겼습니다. BO 관리자가 확인 후 해제해야 합니다.';
  if (!runtime.capabilities[capability]) return '이 기기에서는 해당 기능이 확인되지 않았습니다.';
  return null;
}

export function devicePowerBlockReason(device: PowerSubject & { online: boolean }): string | null {
  const runtimeBlocked = deviceRuntimeBlockReason(device.runtime, 'power');
  if (runtimeBlocked) return runtimeBlocked;
  if (device.online !== true) return '오프라인 기기는 전원 제어를 사용할 수 없습니다.';
  if (device.powerCapable === false) return '이 기기는 Hikvision 전원 제어를 지원하지 않습니다.';
  if (device.powerCapable !== true) return 'Hikvision 전원 SDK 지원이 확인된 기기만 전원 제어를 사용할 수 있습니다.';
  if (device.nativeStatus?.sdkAvailable !== true) return '현재 APK에서 Hikvision 전원 SDK가 확인된 기기만 전원 제어를 사용할 수 있습니다.';
  if (!isVerifiedHikvisionPowerDevice(device)) return '최근 2분 안에 Hikvision 전원 SDK 상태가 확인된 기기만 전원 제어를 사용할 수 있습니다.';
  return null;
}

/** Reboot needs the native bridge first shipped in Android Player 0.4.3. */
export function deviceRebootBlockReason(device: PowerSubject & { online: boolean; appVersion?: string | null }): string | null {
  const runtimeBlocked = deviceRuntimeBlockReason(device.runtime, 'reboot');
  if (runtimeBlocked) return runtimeBlocked;
  const blocked = devicePowerBlockReason(device);
  if (blocked) return blocked;
  const version = /^(\d+)\.(\d+)\.(\d+)$/.exec(device.appVersion ?? '');
  if (!version || !(Number(version[1]) > 0 || Number(version[2]) > 4 || Number(version[2]) === 4 && Number(version[3]) >= 3)) {
    return '기기 재시작은 APK 0.4.3 이상으로 업데이트한 뒤 사용할 수 있습니다.';
  }
  return null;
}

/** Screen capture runs through the Hikvision SDK, so it follows the same class gate. */
export function deviceCaptureBlockReason(device: PowerSubject & { online: boolean }): string | null {
  const runtimeBlocked = deviceRuntimeBlockReason(device.runtime, 'screenCapture');
  if (runtimeBlocked) return runtimeBlocked;
  if (device.online !== true) return '온라인 기기에서만 새 화면을 요청할 수 있습니다.';
  if (device.nativeStatus?.sdkAvailable !== true) return 'Hikvision SDK가 확인된 기기에서만 화면을 캡처할 수 있습니다.';
  return null;
}

export function formatClock(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const h = String(Math.floor(wrapped / 60)).padStart(2, '0');
  const m = String(wrapped % 60).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * One operating window, keyed by the weekday it starts on.
 *
 * `off` earlier than or equal to `on` means the window runs past midnight and
 * ends the following day — transit sites really do run 05:30 to 01:00.
 * A weekday with no entry is a closed day: the panel stays off.
 */
export type PowerScheduleEntry = {
  /** 0 = Sunday … 6 = Saturday, matching `Date.prototype.getDay`. */
  weekday: number;
  /** Local "HH:MM" the panel powers on. */
  on: string;
  /** Local "HH:MM" the panel powers off. */
  off: string;
};

export type DevicePowerSchedule = {
  /** false keeps the stored entries but hands control back to the device's own weekly plan. */
  enabled: boolean;
  entries: PowerScheduleEntry[];
  /**
   * What a Korean public holiday does to the weekly plan. Absent means the
   * holiday is an ordinary weekday. `off` keeps the panel dark for the whole
   * day; `on` replaces that day's window with its own hours, even on a weekday
   * the weekly plan leaves closed.
   */
  holiday?: HolidayPowerRule;
};

export type HolidayPowerRule =
  | { mode: 'off' }
  | { mode: 'on'; on: string; off: string };

/** Local "YYYY-MM-DD" — the key public-holiday lists use. */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DATE_KEY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export function isDateKey(value: unknown): value is string {
  return typeof value === 'string' && DATE_KEY.test(value);
}

function validateHolidayRule(value: unknown): HolidayPowerRule | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as { mode?: unknown; on?: unknown; off?: unknown };
  if (raw.mode === 'off') return { mode: 'off' };
  if (raw.mode !== 'on' || typeof raw.on !== 'string' || typeof raw.off !== 'string') return null;
  const on = parseClock(raw.on);
  const off = parseClock(raw.off);
  if (on === null || off === null) return null;
  return { mode: 'on', on: formatClock(on), off: formatClock(off) };
}

/** Epoch milliseconds for the single slot the panel firmware holds. */
export type PowerWindow = {
  offAt: number;
  onAt: number;
};

/**
 * Outside an operating window the panel is powered down after this delay, not
 * instantly, so the Player can still report and an engineer can step in.
 * Kept short (1 min): an opening that starts inside the grace is treated as
 * "already open" and skips the shutdown, so a long grace made it impossible to
 * test a power-on that begins within the next few minutes (2026-09-18).
 */
export const POWER_SCHEDULE_GRACE_MS = 60_000;

/**
 * How early a panel that is already on joins the coming window instead of
 * being shut down first.
 *
 * Without this the Player powers a running panel down five minutes from now
 * and asks the firmware to wake it again minutes later. That cycle saves no
 * meaningful power — the panel spends most of it booting — and it converts a
 * single missed RTC wake into a whole day of dark screen, which is exactly the
 * failure operators report. Staying on across the gap has no such downside.
 */
export const POWER_SCHEDULE_EARLY_JOIN_MS = 30 * 60_000;

/**
 * What the Player does inside an operating window when no schedule block is
 * playable. `idleScreen` keeps the existing clock/weather stand-by page;
 * `screenOff` drops the backlight instead, which saves the panel but needs the
 * admin list to surface it so a dark screen is never mistaken for a fault.
 */
export type IdleBehavior = 'idleScreen' | 'screenOff';

export const IDLE_BEHAVIORS: readonly IdleBehavior[] = ['idleScreen', 'screenOff'];

export function isIdleBehavior(value: unknown): value is IdleBehavior {
  return typeof value === 'string' && (IDLE_BEHAVIORS as readonly string[]).includes(value);
}

/** Rejects malformed input before it reaches the database or the panel. */
export function validatePowerSchedule(value: unknown): DevicePowerSchedule | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as { enabled?: unknown; entries?: unknown; holiday?: unknown };
  if (typeof raw.enabled !== 'boolean') return null;
  if (!Array.isArray(raw.entries) || raw.entries.length > 7) return null;
  let holiday: HolidayPowerRule | null = null;
  if (raw.holiday !== undefined && raw.holiday !== null) {
    holiday = validateHolidayRule(raw.holiday);
    if (!holiday) return null;
  }

  const seen = new Set<number>();
  const entries: PowerScheduleEntry[] = [];
  for (const item of raw.entries) {
    if (!item || typeof item !== 'object') return null;
    const entry = item as { weekday?: unknown; on?: unknown; off?: unknown };
    if (!Number.isInteger(entry.weekday)) return null;
    const weekday = entry.weekday as number;
    if (weekday < 0 || weekday > 6 || seen.has(weekday)) return null;
    if (typeof entry.on !== 'string' || typeof entry.off !== 'string') return null;
    const on = parseClock(entry.on);
    const off = parseClock(entry.off);
    if (on === null || off === null) return null;
    seen.add(weekday);
    entries.push({ weekday, on: formatClock(on), off: formatClock(off) });
  }
  entries.sort((a, b) => a.weekday - b.weekday);
  return holiday ? { enabled: raw.enabled, entries, holiday } : { enabled: raw.enabled, entries };
}

/** Local midnight of the day `dayOffset` days from `from`. */
function midnight(from: Date, dayOffset: number): Date {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + dayOffset);
  return d;
}

type Window = { start: number; end: number };

/**
 * Every window that could still matter, expanded from yesterday through two
 * weeks ahead. Yesterday is included because an overnight window started then
 * can still be running now.
 *
 * A holiday rule applies to the window that *starts* on the holiday. An
 * overnight window opened the evening before runs to its normal end.
 */
function expandWindows(schedule: DevicePowerSchedule, now: Date, holidays: ReadonlySet<string>): Window[] {
  const byWeekday = new Map(schedule.entries.map(e => [e.weekday, e]));
  const rule = schedule.holiday;
  const windows: Window[] = [];
  for (let offset = -1; offset <= 15; offset += 1) {
    const day = midnight(now, offset);
    const holiday = !!rule && holidays.has(localDateKey(day));
    if (holiday && rule!.mode === 'off') continue;
    const entry = holiday && rule!.mode === 'on' ? rule : byWeekday.get(day.getDay());
    if (!entry) continue;
    const on = parseClock(entry.on);
    const off = parseClock(entry.off);
    if (on === null || off === null) continue;
    const startTime = new Date(day); startTime.setHours(Math.floor(on / 60), on % 60, 0, 0);
    const start = startTime.getTime();
    // An `off` that is not strictly after `on` belongs to the next day.
    const endMinutes = off > on ? off : off + 1440;
    const endTime = new Date(day); endTime.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
    windows.push({ start, end: endTime.getTime() });
  }
  const merged: Window[] = [];
  for (const window of windows.sort((a, b) => a.start - b.start)) {
    const previous = merged.at(-1);
    if (previous && window.start <= previous.end) previous.end = Math.max(previous.end, window.end);
    else merged.push({ ...window });
  }
  return merged;
}

/**
 * The off/on pair to push into the panel's slot.
 *
 * Returns null when the schedule is disabled or empty — the caller should then
 * clear the slot and let the device's own weekly plan take over.
 *
 * Inside an operating window the panel is left alone until that window ends.
 * Outside one the panel is running when the operator said it should not be, so
 * it is powered down after a short grace period rather than immediately; that
 * leaves room for the Player to report state and for an engineer to intervene.
 *
 * A panel that is on within {@link POWER_SCHEDULE_EARLY_JOIN_MS} of the next
 * opening counts as already inside that window, so it is never shut down just
 * to be woken again a few minutes later.
 */
export function nextPowerWindow(
  schedule: DevicePowerSchedule,
  nowMs: number = Date.now(),
  graceMs: number = POWER_SCHEDULE_GRACE_MS,
  earlyJoinMs: number = POWER_SCHEDULE_EARLY_JOIN_MS,
  holidays: Iterable<string> = [],
): PowerWindow | null {
  if (!schedule.enabled || schedule.entries.length === 0) return null;
  const now = new Date(nowMs);
  const windows = expandWindows(schedule, now, new Set(holidays));
  if (windows.length === 0) return null;

  // Stay on if the next opening is near enough that shutting down would only
  // buy a reboot. The grace period is the floor, never a stricter limit.
  const joinMs = Math.max(graceMs, earlyJoinMs);
  const current = windows.find(w => w.start <= nowMs + joinMs && nowMs < w.end);
  const offAt = current ? current.end : nowMs + graceMs;
  const next = windows.find(w => w.start > offAt);
  if (!next) return null;
  return { offAt, onAt: next.start };
}

/** Human-readable summary for the admin list column. */
export function describePowerSchedule(schedule: DevicePowerSchedule | null): string {
  if (!schedule || !schedule.enabled || schedule.entries.length === 0) return '미설정';
  const holiday = schedule.holiday?.mode === 'off' ? ' · 공휴일 끔'
    : schedule.holiday?.mode === 'on' ? ` · 공휴일 ${schedule.holiday.on}~${schedule.holiday.off}` : '';
  return describeWeekly(schedule) + holiday;
}

function describeWeekly(schedule: DevicePowerSchedule): string {
  const labels = ['일', '월', '화', '수', '목', '금', '토'];
  const uniform = schedule.entries.every(
    e => e.on === schedule.entries[0]!.on && e.off === schedule.entries[0]!.off,
  );
  if (uniform && schedule.entries.length === 7) {
    return `매일 ${schedule.entries[0]!.on}~${schedule.entries[0]!.off}`;
  }
  if (uniform) {
    const days = schedule.entries.map(e => labels[e.weekday]).join('');
    return `${days} ${schedule.entries[0]!.on}~${schedule.entries[0]!.off}`;
  }
  return schedule.entries
    .map(e => `${labels[e.weekday]} ${e.on}~${e.off}`)
    .join(', ');
}
